import {
  forwardRef,
  ForwardRefExoticComponent,
  MutableRefObject,
  RefAttributes,
  useCallback,
} from "react";

import * as React from "react";
import ReactModal from "react-modal";
import cn from "classnames";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import ModalHeader from "./modal-header";
import ModalBody from "./modal-body";
import ModalFooter from "./modal-footer";
import { ModalContextProvider } from "./modal-context";

import "./modal.scss";

/**
 *
 * NOTE: the closeDuration and openDurations are references to duration
 * constants outlined in core/maps/_animation-durations.scss (the openDuration
 * value is not used within the component but is here for reference).
 *
 * closeDuration and openDuration should match the "snap" and "fast" values
 * stipulated in the core/maps/_animation-durations.scss file
 *
 */
enum ANIMATION_PROPS {
  // closeDuration is used to set ReactModal close in milliseconds
  closeDuration = 100, // "snap"
  // className that removes all transitions
  disableAnimation = "disable-animations",
  forwardAnimate = "forward-animate",
  // openDuration is not used in the component, it is here to illuminate the scss mappings being used
  openDuration = 200, // "fast"
  reverseAnimate = "reverse-animate",
}

export interface ModalProps extends ReactModal.Props {
  disableAnimations?: boolean;
  appElement?: string | HTMLElement;
  children: React.ReactNode;
  className?: string;
  shouldCloseOnOverlayClick?: boolean;
  isOpen: boolean;
  onClose: (event: React.MouseEvent | React.KeyboardEvent) => void;
  modalTitle: string;
  parentSelector?(): HTMLElement;
  toggleButtonRef?: React.RefObject<HTMLElement>;
}

export interface ModalComposite
  extends ForwardRefExoticComponent<
    ModalProps & RefAttributes<HTMLDivElement>
  > {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
}

const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      disableAnimations = false,
      appElement = "#root",
      children,
      className = "",
      shouldCloseOnOverlayClick = true,
      isOpen,
      onClose,
      onAfterClose: consumerOnAfterClose,
      modalTitle,
      parentSelector = undefined,
      toggleButtonRef,
      ...rest
    }: ModalProps,
    ref,
  ) => {
    const contentRefCallback = useCallback(
      (node: HTMLDivElement) => {
        if (ref) {
          if (typeof ref === "function") {
            ref(node);
          } else {
            (ref as MutableRefObject<HTMLDivElement>).current = node;
          }
        }
      },
      [ref],
    );

    const baseClassName = "eb-modal";
    const getBlocksWith = createBemBlockBuilder([className, baseClassName]);

    const closeDuration: number = disableAnimations
      ? 0
      : ANIMATION_PROPS.closeDuration;
    ReactModal.setAppElement(appElement);

    if (appElement === "body") {
      console.error(
        'Applying the aria-hidden="true" attribute to your <body> element hides your entire web page from assistive technology users!',
      );
    }

    const modalContext = {
      getBlocksWith,
      onClose,
    };

    return (
      <ModalContextProvider value={modalContext}>
        <ReactModal
          bodyOpenClassName={getBlocksWith("--open")}
          className={{
            base: cn(
              getBlocksWith(),
              disableAnimations &&
                getBlocksWith(`--${ANIMATION_PROPS.disableAnimation}`),
            ),
            afterOpen: cn(getBlocksWith(`--${ANIMATION_PROPS.forwardAnimate}`)),
            beforeClose: cn(
              getBlocksWith(`--${ANIMATION_PROPS.reverseAnimate}`),
            ),
          }}
          closeTimeoutMS={closeDuration}
          contentLabel={modalTitle}
          isOpen={isOpen}
          onRequestClose={onClose}
          overlayClassName={{
            base: cn(
              getBlocksWith("__overlay"),
              disableAnimations &&
                getBlocksWith(`__overlay--${ANIMATION_PROPS.disableAnimation}`),
            ),
            afterOpen: getBlocksWith(
              `__overlay--${ANIMATION_PROPS.forwardAnimate}`,
            ),
            beforeClose: getBlocksWith(
              `__overlay--${ANIMATION_PROPS.reverseAnimate}`,
            ),
          }}
          shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
          parentSelector={parentSelector}
          data={{ auto: "modal" }}
          contentRef={contentRefCallback}
          onAfterClose={() => {
            toggleButtonRef &&
              toggleButtonRef.current &&
              toggleButtonRef.current.focus();
            typeof consumerOnAfterClose === "function" &&
              consumerOnAfterClose();
          }}
          {...rest}
        >
          {children}
        </ReactModal>
      </ModalContextProvider>
    );
  },
) as ModalComposite;

Modal.displayName = "Modal";
Modal.Header = ModalHeader;
Modal.Header.displayName = "Modal.Header";
Modal.Body = ModalBody;
Modal.Body.displayName = "Modal.Body";
Modal.Footer = ModalFooter;
Modal.Footer.displayName = "Modal.Footer";

export default Modal;
